import { BackendAuthResponse, BackendAuthUser, BackendUser, BackendEvent, BackendNotification, BackendRating } from './backend-types';

// Validation error class
export class ValidationError extends Error {
  constructor(message: string, public errors: any) {
    super(message);
    this.name = 'ValidationError';
  }
}

// Basic type guards
const isString = (value: unknown): value is string => typeof value === 'string';
const isNumber = (value: unknown): value is number => typeof value === 'number' && !isNaN(value);
const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';
const isObject = (value: unknown): value is object => value !== null && typeof value === 'object';
const isArray = (value: unknown): value is unknown[] => Array.isArray(value);

// Utility function for safe validation
function safeValidate<T>(
  data: unknown,
  entityName: string,
  validator: (data: unknown) => T
): T {
  try {
    return validator(data);
  } catch (error) {
    throw new ValidationError(
      `Validation failed for ${entityName}: ${error instanceof Error ? error.message : 'Unknown error'}`,
      error
    );
  }
}

// Utility function for optional validation
function safeValidateOptional<T>(
  data: unknown,
  entityName: string,
  validator: (data: unknown) => T
): T | undefined {
  if (data === undefined || data === null) {
    return undefined;
  }
  try {
    return validator(data);
  } catch (error) {
    console.warn(`Optional validation failed for ${entityName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    return undefined;
  }
}

// Validation functions with more flexible validation
export const validateAuthResponse = (data: unknown): BackendAuthResponse => {
  if (!isObject(data)) {
    throw new Error('AuthResponse must be an object');
  }
  
  const d = data as any;
  if (
    isBoolean(d.success) &&
    isString(d.accessToken) &&
    isString(d.idToken) &&
    isString(d.refreshToken) &&
    isNumber(d.expiresIn) &&
    d.tokenType === "Bearer"
  ) {
    return d as BackendAuthResponse;
  }
  
  throw new Error('Invalid AuthResponse structure');
};

export const validateAuthUser = (data: unknown): BackendAuthUser => {
  if (!isObject(data)) {
    throw new Error('AuthUser must be an object');
  }
  
  const d = data as any;
  if (
    isString(d.cognitoId) &&
    isString(d.email) &&
    isBoolean(d.banned) &&
    ["NONE", "TEMPORARY", "PERMANENT"].includes(d.banStatus) &&
    (d.id === undefined || isNumber(d.id)) &&
    (d.banReason === undefined || isString(d.banReason))
  ) {
    return d as BackendAuthUser;
  }
  
  throw new Error('Invalid AuthUser structure');
};

export const validateUser = (data: unknown): BackendUser => {
  if (!isObject(data)) {
    throw new Error('User must be an object');
  }
  
  const d = data as any;
  
  // Check only essential required fields - more flexible validation
  if (
    !isNumber(d.id) ||
    !isString(d.email)
  ) {
    throw new Error('User must have valid id and email');
  }
  
  // Make firstName and lastName optional with fallbacks
  if (!isString(d.firstName)) d.firstName = '';
  if (!isString(d.lastName)) d.lastName = '';
  
  // Ensure boolean fields with defaults
  d.banned = Boolean(d.banned);
  
  // Ensure numeric fields with defaults
  d.userPhone = Number(d.userPhone) || 0;
  d.userPhoneCode = String(d.userPhoneCode || '');
  
  // Ensure string fields with defaults
  d.country = d.country || '';
  d.birthDate = d.birthDate || '';
  d.createdAt = d.createdAt || '';
  d.updatedAt = d.updatedAt || '';
  d.banStatus = d.banStatus || 'NONE';
  
  return d as BackendUser;
};

export const validateEvent = (data: unknown): BackendEvent => {
  if (!isObject(data)) {
    throw new Error('Event must be an object');
  }
  
  const d = data as any;
  
  // Check only absolutely essential fields - much more flexible
  if (
    !isNumber(d.id) ||
    !isString(d.name)
  ) {
    throw new Error('Event must have valid id and name');
  }
  
  // Make most fields optional with defaults
  d.eventTypeId = Number(d.eventTypeId) || 1;
  d.eventSubTypeId = Number(d.eventSubTypeId) || 1;
  d.description = d.description || '';
  d.duration = d.duration || '1:00';
  d.guests = Number(d.guests) || 0;
  d.amount = Number(d.amount) || 0;
  d.inscription = Number(d.inscription) || 0;
  d.price = d.price ? Number(d.price) : undefined;
  d.active = Boolean(d.active);
  d.startDate = d.startDate || new Date().toISOString();
  d.address = d.address || '';
  d.space = d.space || '';
  d.hostAssists = Boolean(d.hostAssists);
  
  // Make paymentType flexible - default to FREE if not provided or invalid
  if (!["FREE", "GRUPS", "OTHER"].includes(d.paymentType)) {
    d.paymentType = "FREE";
  }
  
  // Ensure nested objects with defaults
  if (!isObject(d.eventType)) {
    d.eventType = {
      id: d.eventTypeId || 1,
      name: 'Default Type',
      subType: {
        id: d.eventSubTypeId || 1,
        name: 'Default Subtype'
      }
    };
  } else if (!isObject(d.eventType.subType)) {
    d.eventType.subType = {
      id: d.eventSubTypeId || 1,
      name: 'Default Subtype'
    };
  }
  
  // Ensure arrays with defaults
  d.imageUrls = Array.isArray(d.imageUrls) ? d.imageUrls : [];
  d.images = Array.isArray(d.images) ? d.images : [];
  
  // Ensure numeric fields with defaults
  d.currentParticipants = Number(d.currentParticipants) || 0;
  
  return d as BackendEvent;
};

export const validateNotification = (data: unknown): BackendNotification => {
  if (!isObject(data)) {
    throw new Error('Notification must be an object');
  }
  
  const d = data as any;
  if (
    isNumber(d.id) &&
    isString(d.title) &&
    isString(d.body) &&
    isBoolean(d.read) &&
    isString(d.createdAt) &&
    (d.eventId === undefined || isNumber(d.eventId)) &&
    (d.senderId === undefined || isNumber(d.senderId))
  ) {
    return d as BackendNotification;
  }
  
  throw new Error('Invalid Notification structure');
};

export const validateRating = (data: unknown): BackendRating => {
  if (!isObject(data)) {
    throw new Error('Rating must be an object');
  }
  
  const d = data as any;
  if (
    isNumber(d.id) &&
    isNumber(d.eventId) &&
    isNumber(d.userId) &&
    isNumber(d.hostId) &&
    isNumber(d.rating) &&
    isString(d.createdAt) &&
    (d.comment === undefined || isString(d.comment)) &&
    (d.anonymous === undefined || isBoolean(d.anonymous))
  ) {
    return d as BackendRating;
  }
  
  throw new Error('Invalid Rating structure');
};

// Array validation
export const validateArrayData = <T>(
  data: unknown,
  entityName: string,
  validator: (item: unknown) => T
): T[] => {
  if (!isArray(data)) {
    console.warn(`Expected array for ${entityName}, received: ${typeof data}. Returning empty array.`);
    return [];
  }
  
  return data.map((item, index) => {
    try {
      return validator(item);
    } catch (error) {
      console.warn(`Validation failed for ${entityName}[${index}]:`, error);
      // Return null/undefined for invalid items instead of throwing
      return null;
    }
  }).filter(item => item !== null) as T[];
};

export { safeValidate, safeValidateOptional };
